import React, { useState, useContext, useEffect } from "react";
import { Tabs } from "@shopify/polaris";

import constant from "lib/constant/constant";
import { SkeletonAnnotated, Spinner } from "lib/components";

import { storageHelper } from "lib/helpers";
import { PrivateContext } from "lib/context";

import { GenericShopifyCallback } from "app/productOld/modules/generic";
import OperatorAppPermission from "app/appPermission/modules/operator/appPermission";
import { Commission, Finish, UpdateProfile } from "./features";

import tabs from "./stepperOnboardingConfig";

const { onboardingSteps } = constant;
const { APP_PERMISSION, COMMISSION, FINISH, UPDATE_PROFILE } = onboardingSteps;

const tabIdToSetupProgress = {
  [UPDATE_PROFILE]: "businessInfo",
  [COMMISSION]: "commission",
  [APP_PERMISSION]: "appPermission",
  [FINISH]: "finish",
};

const OperatorStepperOnboarding = () => {
  const { currentUser, isLoading, setOnboardingTab } = useContext(PrivateContext);
  const { ecommercePlatform = "" } = currentUser || {};
  const initialSelectedTab = storageHelper.get("setupProgress") || "businessInfo";
  const path = location.pathname;

  const indexToTabKeyMapper = {
    welcome: UPDATE_PROFILE,
    about: UPDATE_PROFILE,
    businessInfo: UPDATE_PROFILE,
    commission: COMMISSION,
    term: APP_PERMISSION,
    appPermission: APP_PERMISSION,
    finish: FINISH,
  };

  let updatedTabs = tabs;

  if (ecommercePlatform === constant.BIGCOMMERCE) {
    const index = tabs.findIndex((item) => item.id === APP_PERMISSION);
    if (index > -1) {
      tabs.splice(index, 1);
    }
    updatedTabs = tabs.map((tab) => {
      if (tab.id === FINISH) {
        return {
          ...tab,
          tabIndex: tab.tabIndex - 1,
        };
      }
      return tab;
    });
    delete indexToTabKeyMapper.appPermission;
  }

  const findTab = (key) => updatedTabs.find((tab) => tab.id === key);
  const tabKey = indexToTabKeyMapper[initialSelectedTab];
  const currentTab = findTab(tabKey) || findTab(UPDATE_PROFILE);
  const [selected, setSelected] = useState((currentTab && currentTab.id) || UPDATE_PROFILE);
  const [selectedTabIndex, setSelectedTabIndex] = useState(currentTab && currentTab.tabIndex);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [selected]);

  const changeTab = (value) => {
    setSelected(value);
    const activeTab = findTab(value);
    setSelectedTabIndex(activeTab && activeTab.tabIndex);
    const setupProgress = tabIdToSetupProgress[value];
    if (value !== FINISH && setupProgress) {
      storageHelper.set("setupProgress", setupProgress);
      setOnboardingTab(setupProgress);
    }
  };

  const getOnboardingStep = () => {
    if (isLoading) {
      if (selected === FINISH) {
        return <Spinner />;
      }
      return <SkeletonAnnotated primaryLines={15} />;
    }
    switch (selected) {
      case UPDATE_PROFILE:
        return <UpdateProfile setSelectedTab={changeTab} />;
      case COMMISSION:
        return <Commission setSelectedTab={changeTab} />;
      case APP_PERMISSION:
        if (path === "/shopify/callback") {
          return <GenericShopifyCallback />;
        }
        return <OperatorAppPermission setSelectedTab={changeTab} />;
      case FINISH:
        return <Finish />;
      default:
        return null;
    }
  };

  const topMargin25px = {
    marginTop: "25px",
  };

  return (
    <>
      <Tabs tabs={updatedTabs} selected={selectedTabIndex} fitted />
      <div style={topMargin25px}>{getOnboardingStep()}</div>
    </>
  );
};

export default OperatorStepperOnboarding;
